import { CSSProperties } from "react"
import { isSameDay, isWithinInterval } from "date-fns"
import {
	DefinedRangeType,
	InternalDate,
	RangeCalendar,
	RangeDate,
} from "./MultiRangeCalendar.domain"
import { CUSTOM_RANGES_PRESET } from "./CalendarPresets/Presets"
import { isDefined } from "helpers/objects"
import { formatDateWithShortMonth } from "helpers/temporal"

const CLASS_DAY_IN_RANGE = "multiple-date-range__day--in-range"
const CLASS_DAY_IN_RANGE_START = "multiple-date-range__day--in-range--start"
const CLASS_DAY_IN_RANGE_END = "multiple-date-range__day--in-range--end"
const DATES_RANGE_SEPARATOR = "\xa0\xa0|\xa0\xa0"

const STYLES_BORDER_PARENT: CSSProperties = {
	padding: "1rem",
	margin: "-1rem",
	height: "20px",
	width: "20px",
	lineHeight: "20px",
}

const STYLES_BORDER_CHILD: CSSProperties = {
	borderRadius: "50%",
	color: "white",
	textAlign: "center",
	display: "inline-block",
}

export const isSameDayInternalDate = (
	dateLeft: InternalDate,
	dateRight: InternalDate
) => {
	if (isSomeNull(dateLeft, dateRight)) {
		return false
	}

	return isSameDay(dateLeft as Date, dateRight as Date)
}

export const getBorderRangeClass = (
	currentDate: InternalDate,
	range: RangeCalendar
): {
	className: string
	stylesParent: CSSProperties
	stylesChild: CSSProperties
} => {
	const { startDate, endDate, color, secondaryColor } = range

	const styledParentChild = {
		...STYLES_BORDER_CHILD,
		backgroundColor: color,
	}

	if (isSameDayInternalDate(startDate, currentDate)) {
		return {
			className: `${CLASS_DAY_IN_RANGE} ${CLASS_DAY_IN_RANGE_START}`,
			stylesParent: {
				...STYLES_BORDER_PARENT,
				background: `linear-gradient(90deg, transparent 50%, ${secondaryColor} 50%)`,
			},
			stylesChild: styledParentChild,
		}
	}

	if (isSameDayInternalDate(endDate, currentDate)) {
		return {
			className: `${CLASS_DAY_IN_RANGE} ${CLASS_DAY_IN_RANGE_END}`,
			stylesParent: {
				...STYLES_BORDER_PARENT,
				background: `linear-gradient(90deg, ${secondaryColor} 50%, transparent 50%)`,
			},
			stylesChild: styledParentChild,
		}
	}

	return {
		className: CLASS_DAY_IN_RANGE,
		stylesParent: {
			lineHeight: "20px",
		},
		stylesChild: { backgroundColor: secondaryColor },
	}
}

export const getCircularPos = (length: number, next: number) =>
	((next % length) + length) % length

const isSomeNull = <T>(...parameters: T[]) =>
	parameters.some((parameter: T) => parameter === null)

export const getIndexCalendarInDateReduce = (date: Date) => (
	calendarIndexWithinInterval: number[],
	currentCalendarRange: RangeCalendar,
	calendarIndex: number
): number[] => {
	const { startDate: start, endDate: end } = currentCalendarRange

	if (isSomeNull(start, end)) {
		return calendarIndexWithinInterval
	}

	if (isWithinInterval(date, { start: start as Date, end: end as Date })) {
		return [...calendarIndexWithinInterval, calendarIndex]
	}

	return calendarIndexWithinInterval
}

export const addCustomRangePreset = (
	definedRanges: DefinedRangeType[]
): DefinedRangeType[] => {
	const haveCustomRanges = definedRanges.findIndex(
		(definedRanges) => definedRanges.id === CUSTOM_RANGES_PRESET.id
	)
	return definedRanges.concat(
		haveCustomRanges === -1 ? CUSTOM_RANGES_PRESET : []
	)
}

export const setDateCalendarRange = (
	ranges: RangeCalendar[],
	[startDate, endDate]: RangeDate,
	calendarIndex: number
) =>
	Object.assign([], ranges, {
		[calendarIndex]: {
			...ranges[calendarIndex],
			startDate,
			endDate,
		},
	})

const formatDateCalendar = (date: InternalDate, locale: string) => {
	if (!isDefined(date)) {
		return ""
	}

	return formatDateWithShortMonth(date, locale).replace(".", "")
}

const formatRangeTextInput = (locale: string) => (range: RangeCalendar) => {
	const { startDate, endDate } = range

	const startDateFormatted = formatDateCalendar(startDate, locale)
	const endDateFormatted = formatDateCalendar(endDate, locale)

	return `${startDateFormatted} - ${endDateFormatted}`
}

export const formatRangesTextInput = (
	ranges: RangeCalendar[],
	locale: string
) => ranges.map(formatRangeTextInput(locale)).join(DATES_RANGE_SEPARATOR)
